import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { bibleApi } from '../api/bibleApi';
import type { Verse } from '../../features/bible/types';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { getVerseKey } from '../../features/bible/utils/verseUtils';
import { authService } from '../services/authService';
import { ErrorService } from '../services/errorService';
import { profileApi } from '../api/profileApi';

interface Bookmark {
  id: string;
  book: string;
  chapter: number;
  verse: number;
  created_at: string;
}

interface BibleStore {
  bookmarks: Bookmark[];
  verseContents: Record<string, string>;
  bookmarkStatuses: Record<string, boolean>;
  isLoading: boolean;
  loadingChapters: Set<string>;
  loadingVerses: Set<string>;
  selectedVerse: Verse | null;
  selectedVerseText: string | null;
  isInfoSidebarOpen: boolean;
  selectedChapter: { book: string; chapter: string } | null;
  isBibleView: boolean;
  lastReadPosition: { book: string; chapter: string } | null;
  
  // Actions
  loadChapterVerses: (book: string, chapter: string, version: string) => Promise<void>;
  getVerseContent: (book: string, chapter: string, verse: string, version?: string, loadFullChapter?: boolean) => Promise<string>;
  setSelectedVerse: (verse: Verse | null) => Promise<void>;
  setInfoSidebarOpen: (isOpen: boolean) => void;
  setIsBibleView: (isBibleView: boolean) => void;
  setSelectedChapter: (book: string, chapter: string) => void;
  saveReadingPosition: (book: string, chapter: string) => Promise<void>;
  syncReadingPosition: () => Promise<void>;
  loadBookmarks: () => Promise<void>;
  toggleBookmark: (verse: Verse, reference: string) => Promise<void>;
  deleteBookmark: (id: string) => Promise<void>;
}

export const useBibleStore = create<BibleStore>()(
  persist(
    devtools((set, get) => ({
      bookmarks: [],
      verseContents: {},
      bookmarkStatuses: {},
      isLoading: false,
      loadingChapters: new Set(),
      loadingVerses: new Set(),
      selectedVerse: null,
      selectedVerseText: null,
      isInfoSidebarOpen: false,
      selectedChapter: null,
      isBibleView: false,
      lastReadPosition: null,

      loadChapterVerses: async (book, chapter, version) => {
        const chapterKey = `${book}-${chapter}`;
        
        // If this chapter is already loading, don't start another load
        if (get().loadingChapters.has(chapterKey)) {
          return;
        }

        try {
          set(state => ({ 
            isLoading: true,
            loadingChapters: new Set([...state.loadingChapters, chapterKey])
          }));

          // Use batch fetching for the entire chapter
          const verses = await bibleApi.getBatchVerseContent(book, chapter, 
            Array.from({ length: 200 }, (_, i) => (i + 1).toString()), version);
          
          set(state => ({
            verseContents: { ...state.verseContents, ...verses }
          }));
        } catch (error) {
          console.error('Error loading chapter verses:', error);
        } finally {
          set(state => ({ 
            isLoading: false,
            loadingChapters: new Set([...state.loadingChapters].filter(k => k !== chapterKey))
          }));
        }
      },

      getVerseContent: async (book, chapter, verse, version, loadFullChapter = false) => {
        const verseKey = `${book}-${chapter}-${verse}`;
        const chapterKey = `${book}-${chapter}`;
        const cachedContent = get().verseContents[verseKey];
        
        if (cachedContent) {
          return cachedContent;
        }

        // If this chapter or verse is already loading, wait a bit and check the cache again
        if (get().loadingChapters.has(chapterKey) || get().loadingVerses.has(verseKey)) {
          await new Promise(resolve => setTimeout(resolve, 100)); // Wait 100ms
          const updatedContent = get().verseContents[verseKey];
          if (updatedContent) {
            return updatedContent;
          }
        }

        try {
          if (loadFullChapter) {
            // Load the entire chapter
            set(state => ({
              loadingChapters: new Set([...state.loadingChapters, chapterKey])
            }));

            const verses = await bibleApi.getBatchVerseContent(book, chapter, 
              Array.from({ length: 200 }, (_, i) => (i + 1).toString()), version);
            
            set(state => ({
              verseContents: { ...state.verseContents, ...verses },
              loadingChapters: new Set([...state.loadingChapters].filter(k => k !== chapterKey))
            }));

            return verses[verseKey] || '';
          } else {
            // Load just this verse
            set(state => ({
              loadingVerses: new Set([...state.loadingVerses, verseKey])
            }));

            const verses = await bibleApi.getBatchVerseContent(book, chapter, [verse], version);
            
            set(state => ({
              verseContents: { ...state.verseContents, ...verses },
              loadingVerses: new Set([...state.loadingVerses].filter(k => k !== verseKey))
            }));

            return verses[verseKey] || '';
          }
        } catch (error) {
          console.error('Error fetching verse content:', error);
          return '';
        } finally {
          // Clear loading state in case of error
          set(state => ({
            loadingVerses: new Set([...state.loadingVerses].filter(k => k !== verseKey)),
            loadingChapters: new Set([...state.loadingChapters].filter(k => k !== chapterKey))
          }));
        }
      },

      setSelectedVerse: async (verse: Verse | null) => {
        try {
          set({ selectedVerse: verse });
          if (verse) {
            const { content, isBookmarked } = await bibleApi.getVerseDetails(
              verse.book,
              verse.chapter,
              verse.verse
            );
            set({
              selectedVerse: verse,
              selectedVerseText: content,
              isInfoSidebarOpen: true,
              bookmarkStatuses: {
                ...get().bookmarkStatuses,
                [`${verse.book}-${verse.chapter}-${verse.verse}`]: isBookmarked
              }
            });
          }
        } catch (error) {
          ErrorService.handle(error, {
            severity: 'low',
            userMessage: 'Unable to load verse details',
            context: { verse }
          });
        }
      },

      setInfoSidebarOpen: (isOpen) => set((state) => ({ 
        isInfoSidebarOpen: state.isBibleView ? true : isOpen 
      })),

      setIsBibleView: (isBibleView) => set({ isBibleView }),

      setSelectedChapter: (book, chapter) => set(state => ({ 
        selectedChapter: { book, chapter },
        selectedVerse: null,
        selectedVerseText: null,
        isInfoSidebarOpen: state.isInfoSidebarOpen
      })),

      setLastReadPosition: (book: string, chapter: string) => 
        set({ lastReadPosition: { book, chapter } }),

      loadBookmarks: async () => {
        set({ isLoading: true });
        try {
          const { bookmarks, error } = await authService.getBookmarks();
          if (error) throw error;
          set({ bookmarks });
        } finally {
          set({ isLoading: false });
        }
      },

      toggleBookmark: async (verse, reference) => {
        const key = `${verse.book}-${verse.chapter}-${verse.verse}`;
        const currentStatus = get().bookmarkStatuses[key];
        
        try {
          set({ isLoading: true });
          const { bookmarks, error } = await authService.toggleBookmark(verse, reference);
          if (error) throw error;
          
          const newStatus = !currentStatus;
          set({
            bookmarks,
            bookmarkStatuses: {
              ...get().bookmarkStatuses,
              [key]: newStatus
            }
          });
        } finally {
          set({ isLoading: false });
        }
      },

      deleteBookmark: async (id: string) => {
        try {
          set({ isLoading: true });
          const { error } = await authService.deleteBookmark(id);
          if (error) throw error;
          set(state => ({
            bookmarks: state.bookmarks.filter(b => b.id !== id)
          }));
        } finally {
          set({ isLoading: false });
        }
      },

      syncReadingPosition: async () => {
        const { position, error } = await authService.getReadingPosition();
        if (error) return;
        
        // Set default position if none exists
        const defaultPosition = {
            book: 'Genesis',
            chapter: '1'
        };
        
        set({ 
            lastReadPosition: position?.last_read_book && position?.last_read_chapter 
                ? { 
                    book: position.last_read_book, 
                    chapter: position.last_read_chapter 
                  }
                : defaultPosition 
        });
      },

      saveReadingPosition: async (book: string, chapter: string) => {
        try {
          await profileApi.saveReadingPosition(book, chapter);
          set({ lastReadPosition: { book, chapter } });
        } catch (error) {
          console.error('Failed to save reading position:', error);
        }
      }
    })),
    {
      name: 'bible-store',
      partialize: (state) => ({
        bookmarkStatuses: state.bookmarkStatuses,
        lastReadPosition: state.lastReadPosition
      })
    }
  )
);